'use strict'

import React from 'react'
import PropTypes from 'prop-types'
import { PERIOD_LABELS, PERIOD_MONTHLY, PERIOD_QUARTER, PERIOD_YEAR } from '../extra/constants'

/**
 * チップの class を決める。必須 は背景色つき、任意 は枠線のみ（マトリクスのセルと同じ区別）。
 *
 * @param {'set' | 'unset' | 'remove'} variant
 * @param {boolean} isOptional
 */
function chipClassOf(variant, isOptional) {
  if (isOptional) {
    return variant === 'set'
      ? 'border border-green-500 text-green-800'
      : variant === 'unset'
        ? 'border border-red-500 text-red-800'
        : 'border border-gray-400 text-gray-800'
  }
  return variant === 'set'
    ? 'bg-green-100 text-green-800'
    : variant === 'unset'
      ? 'bg-red-100 text-red-800'
      : 'bg-gray-300 text-gray-800'
}

/** 年次・四半期時: 月次 vs 主タスクの2段表示（チップの色は variant で統一） */
function MappingSourceSplitChips({ monthlyItems, primaryPeriodLabel, primaryItems, variant, optionalNameSet }) {
  const renderBlock = (label, items, keyPrefix) => (
    <div className="mb-2 last:mb-0 flex flex-wrap items-start gap-x-2 gap-y-1">
      <span className="shrink-0 text-h7 font-bold pt-0.5 text-[#3150a5]">{label}</span>
      <div className="flex min-w-0 flex-1 flex-wrap gap-2 items-center">
        {items.length === 0 ? (
          <span className="text-h7 text-gray-400">-</span>
        ) : (
          items.map((item, i) => (
            <span
              key={`${keyPrefix}-${i}`}
              className={`inline-block px-3 py-1 rounded-full text-sm ${chipClassOf(variant, optionalNameSet.has(item))}`}
            >
              {item}
            </span>
          ))
        )}
      </div>
    </div>
  )

  return (
    <>
      {renderBlock('月次', monthlyItems, `${variant}-m`)}
      {renderBlock(primaryPeriodLabel, primaryItems, `${variant}-p`)}
    </>
  )
}

MappingSourceSplitChips.propTypes = {
  monthlyItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  primaryPeriodLabel: PropTypes.string.isRequired,
  primaryItems: PropTypes.arrayOf(PropTypes.string).isRequired,
  variant: PropTypes.oneOf(['set', 'unset', 'remove']).isRequired,
  optionalNameSet: PropTypes.instanceOf(Set).isRequired
}

/** 汎用マッピング設定の状況モーダル用ボディ（グラフ名クリックで表示）。必須項目・任意項目を「設定済項目」「未設定」「不要」の3グループでチップ表示。任意 は枠線のみのチップで区別する。useModal の body として渡す。 */
const RequiredColumnsModal = ({ dashboardName, graphName, periodKind, requiredColumnsGrouped }) => {
  const setList = requiredColumnsGrouped?.set ?? []
  const unsetList = requiredColumnsGrouped?.unset ?? []
  const removeList = requiredColumnsGrouped?.remove ?? []
  // 任意項目の名称。チップを枠線のみで描くかの判定に使う
  const optionalNameSet = new Set(requiredColumnsGrouped?.optionalNames ?? [])

  const showMappingSourceSplit =
    (periodKind === PERIOD_YEAR || periodKind === PERIOD_QUARTER) && requiredColumnsGrouped?.setFromMonthlyTask != null

  const primaryPeriodLabel = PERIOD_LABELS[periodKind] ?? periodKind ?? ''

  const renderChipsRow = (list, variant, keyPrefix) => (
    <div className="flex flex-wrap gap-2">
      {list.length === 0 ? (
        <span className="text-h7 text-gray-400">-</span>
      ) : (
        list.map((item, i) => (
          <span
            key={`${keyPrefix}-${i}`}
            className={`inline-block px-3 py-1 rounded-full text-sm ${chipClassOf(variant, optionalNameSet.has(item))}`}
          >
            {item}
          </span>
        ))
      )}
    </div>
  )

  return (
    <div className="px-4 pt-5 pb-4 overflow-y-auto flex-1 grey--text">
      <table className="form-table table-fixed w-full required-columns-modal__table">
        <tbody className="text-caption-2">
          {dashboardName !== undefined && dashboardName !== '' && (
            <tr>
              <th className="form-table__col-head required-columns-modal__col-head">
                <span className="text-h7 font-bold">ダッシュボード名</span>
              </th>
              <td className="form-table__col-data">
                <span className="text-h7 font-bold">{dashboardName}</span>
              </td>
            </tr>
          )}
          {graphName && (
            <tr>
              <th className="form-table__col-head required-columns-modal__col-head">
                <span className="text-h7 font-bold">グラフ名</span>
              </th>
              <td className="form-table__col-data">
                <span className="text-h7 font-bold">{graphName}</span>
              </td>
            </tr>
          )}
          <tr>
            <th className="form-table__col-head required-columns-modal__col-head">
              <span className="text-h7 font-bold">設定済項目</span>
            </th>
            <td className="form-table__col-data">
              {showMappingSourceSplit ? (
                <MappingSourceSplitChips
                  monthlyItems={requiredColumnsGrouped.setFromMonthlyTask}
                  primaryPeriodLabel={primaryPeriodLabel}
                  primaryItems={requiredColumnsGrouped.setFromPrimaryTask}
                  variant="set"
                  optionalNameSet={optionalNameSet}
                />
              ) : (
                renderChipsRow(setList, 'set', 'set')
              )}
            </td>
          </tr>
          <tr>
            <th className="form-table__col-head required-columns-modal__col-head">
              <span className="text-h7 font-bold">未設定項目</span>
            </th>
            <td className="form-table__col-data">
              {showMappingSourceSplit ? (
                <MappingSourceSplitChips
                  monthlyItems={requiredColumnsGrouped.unsetFromMonthlyTask}
                  primaryPeriodLabel={primaryPeriodLabel}
                  primaryItems={requiredColumnsGrouped.unsetFromPrimaryTask}
                  variant="unset"
                  optionalNameSet={optionalNameSet}
                />
              ) : (
                renderChipsRow(unsetList, 'unset', 'unset')
              )}
            </td>
          </tr>
          <tr>
            <th className="form-table__col-head required-columns-modal__col-head">
              <span className="text-h7 font-bold">不要項目</span>
            </th>
            <td className="form-table__col-data">
              {showMappingSourceSplit ? (
                <MappingSourceSplitChips
                  monthlyItems={requiredColumnsGrouped.removeFromMonthlyTask}
                  primaryPeriodLabel={primaryPeriodLabel}
                  primaryItems={requiredColumnsGrouped.removeFromPrimaryTask}
                  variant="remove"
                  optionalNameSet={optionalNameSet}
                />
              ) : (
                renderChipsRow(removeList, 'remove', 'remove')
              )}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  )
}

RequiredColumnsModal.propTypes = {
  dashboardName: PropTypes.string,
  graphName: PropTypes.string,
  periodKind: PropTypes.string,
  requiredColumnsGrouped: PropTypes.shape({
    set: PropTypes.arrayOf(PropTypes.string),
    unset: PropTypes.arrayOf(PropTypes.string),
    remove: PropTypes.arrayOf(PropTypes.string),
    setFromMonthlyTask: PropTypes.arrayOf(PropTypes.string),
    setFromPrimaryTask: PropTypes.arrayOf(PropTypes.string),
    unsetFromMonthlyTask: PropTypes.arrayOf(PropTypes.string),
    unsetFromPrimaryTask: PropTypes.arrayOf(PropTypes.string),
    removeFromMonthlyTask: PropTypes.arrayOf(PropTypes.string),
    removeFromPrimaryTask: PropTypes.arrayOf(PropTypes.string),
    optionalNames: PropTypes.arrayOf(PropTypes.string)
  })
}

RequiredColumnsModal.defaultProps = {
  dashboardName: '',
  graphName: '',
  periodKind: PERIOD_MONTHLY,
  requiredColumnsGrouped: { set: [], unset: [], remove: [], optionalNames: [] }
}

export default RequiredColumnsModal
